import React, { useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { blogPosts } from "../contexts/BlogContext"
import { useLanguage } from "../contexts/LanguageContext"
import { Instagram, MapPin, Globe, Star, Phone, Map, Clock, Home, ArrowLeft, ArrowRight, Calendar, User } from "lucide-react"
import "../styles/BlogPost.css"

const BlogPost = () => {
  const { id } = useParams()
  const { language } = useLanguage()
  const isRtl = language === "he"
  const navigate = useNavigate ? useNavigate() : (url) => { window.location.href = url }
  
  // Scroll to top when component mounts or when id changes
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [id])

  const post = blogPosts.find((p) => String(p.id) === String(id))
  if (!post) {
    return (
      <main className={`blog-main${isRtl ? " rtl-text" : ""}`}>
        <div className="blog-header">
          <h1 className="blog-main-title">{language === "he" ? "פוסט לא נמצא" : language === "fr" ? "Article introuvable" : language === "es" ? "Publicación no encontrada" : "Post not found"}</h1>
          <button className="back-button" onClick={() => navigate("/blog")}>
            {language === "he" ? "חזרה לבלוג" : language === "fr" ? "Retour au blog" : language === "es" ? "Volver al blog" : "Back to Blog"}
          </button>
        </div>
      </main>
    )
  }

  // Helper function to get correct details structure
  const getDetails = () => {
    if (!post.details) return null;
    
    // If details are already in the new structure with language keys
    if (post.details[language]) {
      return post.details[language];
    } 
    // Handle old structure where details are directly in the object
    else if (post.details.address) {
      // Check for language-specific address fields
      const addressField = `address${language !== 'he' ? language.charAt(0).toUpperCase() + language.slice(1) : ''}`;
      const address = post.details[addressField] || post.details.address;
      
      // Handle hours if they exist
      let hours = post.details.hours;
      if (hours && typeof hours === 'object') {
        hours = hours[language] || hours.en || hours.he || '';
      }
      
      return {
        address: address,
        phone: post.details.phone,
        kosher: post.details.kosher,
        hours: hours,
        // Include any other fields that might be present
        ...(post.details.website && { website: post.details.website }),
        ...(post.details.email && { email: post.details.email })
      };
    }
    
    return null;
  };

  const details = getDetails();
  const hasDetails = details && (details.address || details.phone || details.kosher || details.hours);
  const hasLinks = post.links && (post.links.instagram || post.links.googleMaps || post.links.tripadvisorEn || post.links.tripadvisorHe);

  return (
    <main className={`blog-main${isRtl ? " rtl-text" : ""}`}>
      <div className="blog-background-pattern"></div>
      <div className="blog-top-padding"></div>
      
      <div className="blog-page-container">
        <div className="blog-header-row">
          <div className="blog-back-button-container">
            <button className="back-button" onClick={() => navigate("/blog")}>
              {language === "he" ? "חזרה לבלוג" : language === "fr" ? "Retour au blog" : language === "es" ? "Volver al blog" : "Back to Blog"}
            </button>
          </div>
          
          <div className="blog-title-container">
            <h1 className="blog-main-title">{post.title[language]}</h1>
            {post.date && (
              <div className="blog-post-meta">
                <div className="meta-item">
                  <Calendar size={16} className="meta-icon" />
                  <span>{post.date}</span>
                </div>
                {post.author && (
                  <div className="meta-item">
                    <User size={16} className="meta-icon" />
                    <span>{post.author}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className="blog-post-layout">
        <div className="blog-post-main">
          <div className="blog-post-header">
            <p className="blog-post-summary">{post.summary[language]}</p>
          </div>
          {/* Modified blog post content structure for better mobile layout */}
          <div className="blog-post-content">
            <div className="blog-post-image-container">
              <img 
                src={post.image} 
                alt={post.title[language]} 
                className="blog-post-image" 
                loading="lazy"
              />
              {post.imageCaption && (
                <div className="image-caption">{post.imageCaption[language]}</div>
              )}
            </div>
            <div className="blog-post-body">
              {post.content[language].split('\n\n').map((paragraph, index) => {
                // Check if paragraph is a heading (starts with # or ##)
                if (paragraph.startsWith('# ')) {
                  return <h2 key={index} className="blog-post-heading">{paragraph.substring(2)}</h2>
                } else if (paragraph.startsWith('## ')) {
                  return <h3 key={index} className="blog-post-subheading">{paragraph.substring(3)}</h3>
                } else {
                  return <p key={index}>{paragraph}</p>
                }
              })}
            </div>
          </div>
        </div>
        
        {(hasDetails || hasLinks) && (
          <aside className="blog-post-sidebar">
            {hasDetails && (
              <div className="blog-details-card">
                <h3 className="sidebar-title">
                  {language === 'he' ? 'פרטים' : 'Details' }
                </h3>
                
                {details.address && (
                  <div className="detail-item">
                    <span className="detail-label">
                      {language === 'he' ? 'כתובת:' : 'Address:'}
                    </span>
                    <a 
                      href={post.links?.googleMaps} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="detail-link"
                    >
                      {details.address}
                    </a>
                  </div>
                )}
                
                {details.phone && (
                  <div className="detail-item">
                    <span className="detail-label">
                      {language === 'he' ? 'טלפון:' : 'Phone:'}
                    </span>
                    <a 
                      href={`tel:${details.phone.replace(/\D/g, '')}`}
                      className="detail-link"
                    >
                      {details.phone}
                    </a>
                  </div>
                )}
                
                {details.kosher && (
                  <div className="detail-item">
                    <span className="detail-label">
                      {language === 'he' ? 'כשרות:' : 'Kosher:'}
                    </span>
                    <span>{details.kosher}</span>
                  </div>
                )}
                
                {details.hours && (
                  <div className="detail-item">
                    <span className="detail-label">
                      {language === 'he' ? 'שעות פתיחה:' : 'Opening hours:'}
                    </span>
                    <div className="opening-hours">
                      {Object.entries(details.hours).map(([day, time]) => (
                        <div key={day} className="opening-hours-row">
                          <span className="day">{day}</span>
                          <span className="time">{time}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {details.website && (
                  <div className="detail-item">
                    <span className="detail-label">
                      {language === 'he' ? 'אתר אינטרנט:' : 'Website:'}
                    </span>
                    <a 
                      href={details.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="detail-link"
                    >
                      {details.website}
                    </a>
                  </div>
                )}
                
                {details.email && (
                  <div className="detail-item">
                    <span className="detail-label">
                      {language === 'he' ? 'דואר אלקטרוני:' : 'Email:'}
                    </span>
                    <a 
                      href={`mailto:${details.email}`} 
                      className="detail-link"
                    >
                      {details.email}
                    </a>
                  </div>
                )}
              </div>
            )}
            
            {hasLinks && (
              <div className="blog-links-card">
                <h3 className="sidebar-title">
                  {language === 'he' ? 'קישורים' : 'Links'}
                </h3>
                <div className="social-links">
                  {post.links?.website && (
                    <a 
                      href={post.links.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="social-link"
                      aria-label={language === 'he' ? 'אתר אינטרנט' : 'Website'}
                    >
                      <Globe className="link-icon" />
                    </a>
                  )}
                  
                  {post.links?.instagram && (
                    <a 
                      href={post.links.instagram} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="social-link"
                      aria-label="Instagram"
                    >
                      <Instagram className="link-icon" />
                    </a>
                  )}
                  
                  {post.links?.googleMaps && (
                    <a 
                      href={post.links.googleMaps} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="social-link"
                      aria-label={language === 'he' ? 'מפת גוגל' : 'Google Maps'}
                    >
                      <MapPin className="link-icon" />
                    </a>
                  )}
                </div>
              </div>
            )}
          </aside>
        )}
      </div>
    </main>
  )
}

export default BlogPost
